import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { dictionariesService, merchantsService } from "../../../services";

type Item = {
  id: string;
  payment_system_id: string;
  payment_system_name?: string;
  direction: string;
  number: string;
  second_number?: string | null;
  holder_name: string;
  bank_id?: string | null;
  payment_form_url?: string | null;
  bank_name?: string | null;
  is_active: boolean;
  wanted_status: string;
  callback_delay_seconds: number;
};
const empty = {
  payment_system_id: "",
  direction: "IN",
  number: "",
  second_number: "",
  payment_form_url: "",
  holder_name: "",
  bank_id: "",
  is_active: true,
  wanted_status: "SUCCESS",
  callback_delay_seconds: 15,
};

export default function SandboxRequisites() {
  const [items, setItems] = useState<Item[]>([]);
  const [systems, setSystems] = useState<any[]>([]);
  const [banks, setBanks] = useState<any[]>([]);
  const [selected, setSelected] = useState<Item | null>(null);
  const [form, setForm] = useState<any>(empty);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [systemFilter, setSystemFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const filteredItems = items.filter((item) => {
    const needle = search.trim().toLowerCase();
    return (
      (!needle ||
        [
          item.number,
          item.second_number,
          item.holder_name,
          item.bank_name,
          item.payment_system_name,
        ].some((value) => value?.toLowerCase().includes(needle))) &&
      (!systemFilter || item.payment_system_id === systemFilter) &&
      (!activeFilter || item.is_active === (activeFilter === "active"))
    );
  });
  const load = async () =>
    setItems(await merchantsService.getSandboxRequisites());
  useEffect(() => {
    void load();
    void Promise.all([
      dictionariesService.getPaymentSystems(),
      dictionariesService.getBanks(),
    ]).then(([s, b]) => {
      setSystems(s);
      setBanks(b);
    });
  }, []);
  const show = (item?: Item) => {
    setSelected(item || null);
    setForm(
      item
        ? {
            ...item,
            bank_id: item.bank_id || "",
            second_number: item.second_number || "",
            payment_form_url: item.payment_form_url || "",
          }
        : empty,
    );
    setOpen(true);
  };
  const save = async () => {
    const data = {
      ...form,
      bank_id: form.bank_id || null,
      second_number: form.second_number || null,
      payment_form_url: form.payment_form_url.trim() || null,
    };
    selected
      ? await merchantsService.updateSandboxRequisite(selected.id, data)
      : await merchantsService.createSandboxRequisite(data);
    setOpen(false);
    await load();
  };
  const remove = async (id: string) => {
    await merchantsService.deleteSandboxRequisite(id);
    await load();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Sandbox-реквизиты</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => show()}>
          Добавить
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField fullWidth label="Поиск по реквизиту, владельцу, банку или методу" value={search} onChange={(event) => setSearch(event.target.value)} />
          <FormControl sx={{ minWidth: 220 }}><InputLabel>Платёжная система</InputLabel><Select value={systemFilter} label="Платёжная система" onChange={(event) => setSystemFilter(event.target.value)}><MenuItem value="">Все</MenuItem>{systems.map((system) => <MenuItem key={system.id} value={system.id}>{system.token}</MenuItem>)}</Select></FormControl>
          <FormControl sx={{ minWidth: 170 }}><InputLabel>Активность</InputLabel><Select value={activeFilter} label="Активность" onChange={(event) => setActiveFilter(event.target.value)}><MenuItem value="">Все</MenuItem><MenuItem value="active">Активные</MenuItem><MenuItem value="inactive">Неактивные</MenuItem></Select></FormControl>
          <Button onClick={() => { setSearch(""); setSystemFilter(""); setActiveFilter(""); }}>Сбросить</Button>
        </Stack>
      </Paper>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ПС</TableCell>
              <TableCell>Направление</TableCell>
              <TableCell>Реквизит</TableCell>
              <TableCell>Payment form URL</TableCell>
              <TableCell>Владелец</TableCell>
              <TableCell>Банк</TableCell>
              <TableCell>Результат</TableCell>
              <TableCell>Задержка</TableCell>
              <TableCell>Активен</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((x) => (
              <TableRow key={x.id}>
                <TableCell>{x.payment_system_name}</TableCell>
                <TableCell>{x.direction}</TableCell>
                <TableCell>{x.number}</TableCell>
                <TableCell>{x.payment_form_url || "—"}</TableCell>
                <TableCell>{x.holder_name}</TableCell>
                <TableCell>{x.bank_name || "Любой"}</TableCell>
                <TableCell>{x.wanted_status}</TableCell>
                <TableCell>{x.callback_delay_seconds} сек.</TableCell>
                <TableCell>{x.is_active ? "Да" : "Нет"}</TableCell>
                <TableCell>
                  <IconButton onClick={() => show(x)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => void remove(x.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selected ? "Редактировать" : "Добавить"} sandbox-реквизит
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <InputLabel>Платёжная система</InputLabel>
              <Select
                label="Платёжная система"
                value={form.payment_system_id}
                onChange={(e) =>
                  setForm({ ...form, payment_system_id: e.target.value })
                }
              >
                {systems.map((x) => (
                  <MenuItem key={x.id} value={x.id}>
                    {x.token}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Направление</InputLabel>
              <Select
                label="Направление"
                value={form.direction}
                onChange={(e) =>
                  setForm({ ...form, direction: e.target.value })
                }
              >
                <MenuItem value="IN">IN</MenuItem>
                <MenuItem value="OUT">OUT</MenuItem>
              </Select>
            </FormControl>
            <TextField
              required
              label="Номер / реквизит"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
            />
            <TextField
              label="Дополнительный номер"
              value={form.second_number}
              onChange={(e) =>
                setForm({ ...form, second_number: e.target.value })
              }
            />
            <TextField
              required
              label="Владелец"
              value={form.holder_name}
              onChange={(e) =>
                setForm({ ...form, holder_name: e.target.value })
              }
            />
            <TextField
              label="Payment form URL (опционально)"
              placeholder="www.google.com"
              value={form.payment_form_url}
              onChange={(e) =>
                setForm({ ...form, payment_form_url: e.target.value })
              }
            />
            <FormControl>
              <InputLabel>Банк</InputLabel>
              <Select
                label="Банк"
                value={form.bank_id}
                onChange={(e) => setForm({ ...form, bank_id: e.target.value })}
              >
                <MenuItem value="">Любой</MenuItem>
                {banks.map((x) => (
                  <MenuItem key={x.id} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Результат</InputLabel>
              <Select
                label="Результат"
                value={form.wanted_status}
                onChange={(e) =>
                  setForm({ ...form, wanted_status: e.target.value })
                }
              >
                <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                <MenuItem value="DECLINED">DECLINED</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Задержка callback, сек."
              value={form.callback_delay_seconds}
              onChange={(e) =>
                setForm({
                  ...form,
                  callback_delay_seconds: Number(e.target.value),
                })
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
              }
              label="Активен"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={() => void save()}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
